/**
 * Outils PowerKick Her pour la responsable de groupe :
 *  - Générateur d'affiche IA dédié PKH (QR vers l'inscription /adhesion)
 *  - Statistiques QR (scans + conversions)
 *  - Calendrier de publications Facebook (mini-planning hebdo)
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, Loader2, Copy, ExternalLink, Download, QrCode, Calendar as CalendarBlank, Trash2 as Trash } from "lucide-react";

const ROSE = "#F43F5E";
const FB_PAGE_URL = "https://www.facebook.com/profile.php?id=61578616646149";
const FB_GROUP_URL = "https://www.facebook.com/groups/1004407851436065";

export default function PkhStudioTools() {
  return (
    <div className="space-y-6">
      <PkhPoster />
      <QrStats />
      <ContentCalendar />
    </div>
  );
}

function PkhPoster() {
  const [f, setF] = useState({ headline: "PowerKick Her", subtitle: "Kickboxing 100 % femmes · 12 semaines", dates: "", location: "Val-d'Or, QC", price_line: "480 $ · places limitées", format: "carre" });
  const [refs, setRefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const loadTpl = useCallback(() => api.get("/poster/templates?kind=pkh").then((r) => setTemplates(r.data.templates || [])).catch(() => {}), []);
  useEffect(() => { loadTpl(); }, [loadTpl]);

  const saveTemplate = async () => {
    const name = window.prompt("Nom du modèle ?", f.headline);
    if (!name) return;
    try { await api.post("/poster/templates", { name, kind: "pkh", format: f.format, fields: f }); toast.success("Modèle enregistré ✓"); loadTpl(); }
    catch { toast.error("Erreur"); }
  };
  const loadTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (t) { setF({ ...f, ...t.fields, format: t.format || f.format }); toast.success(`Modèle « ${t.name} » chargé`); }
  };
  const delTemplate = async (id) => { try { await api.delete(`/poster/templates/${id}`); loadTpl(); } catch { /* */ } };

  const onFiles = async (fl) => {
    const files = Array.from(fl || []).slice(0, 4);
    const b64 = await Promise.all(files.map((file) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file);
    })));
    setRefs(b64);
  };

  const generate = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/poster/pkh", { ...f, reference_images: refs });
      const jobId = data.job_id; let tries = 0;
      const poll = async () => {
        tries += 1;
        const { data: job } = await api.get(`/poster/job/${jobId}`);
        if (job.status === "done") { setResult(job.result); setBusy(false); toast.success("Affiche PowerKick Her générée ✓"); }
        else if (job.status === "error") { toast.error(job.error || "Échec"); setBusy(false); }
        else if (tries > 40) { toast.error("Délai dépassé"); setBusy(false); }
        else setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); setBusy(false); }
  };

  const copyCaption = async () => { try { await navigator.clipboard.writeText(result?.caption || ""); toast.success("Légende copiée ✓"); } catch { toast.error("Impossible"); } };
  const shareFb = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(result?.full_url || result?.url || "")}`, "_blank", "noopener");

  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5" data-testid="pkh-poster-studio">
      <div className="flex items-center gap-2 mb-1"><Sparkles size={18} style={{ color: ROSE }} /><h2 className="font-semibold text-white">Affiche PowerKick Her (IA + QR d'inscription)</h2></div>
      <p className="text-xs text-zinc-400 mb-4">Génère une affiche pour ta session. Le code QR (vers l'inscription PowerKick Her) et les coordonnées sont ajoutés automatiquement, puis partage sur Facebook.</p>
      {templates.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="pkh-templates">
          <span className="text-xs text-zinc-500">Modèles :</span>
          {templates.map((t) => (
            <span key={t.id} className="inline-flex items-center gap-1 text-xs bg-zinc-800 rounded-full pl-3 pr-1 py-1">
              <button onClick={() => loadTemplate(t.id)} data-testid={`pkh-tpl-load-${t.id}`} className="text-rose-300">{t.name}</button>
              <button onClick={() => delTemplate(t.id)} className="text-zinc-500 hover:text-red-400 px-1">×</button>
            </span>
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <Inp label="Titre" v={f.headline} on={(v) => setF({ ...f, headline: v })} testid="pkh-poster-headline" />
        <Inp label="Accroche" v={f.subtitle} on={(v) => setF({ ...f, subtitle: v })} />
        <Inp label="Session / dates" v={f.dates} on={(v) => setF({ ...f, dates: v })} testid="pkh-poster-dates" />
        <Inp label="Lieu" v={f.location} on={(v) => setF({ ...f, location: v })} />
        <Inp label="Prix / urgence" v={f.price_line} on={(v) => setF({ ...f, price_line: v })} />
        <label className="text-xs text-zinc-400">Format
          <select value={f.format} onChange={(e) => setF({ ...f, format: e.target.value })} data-testid="pkh-poster-format" className="mt-1 w-full bg-zinc-800 border border-white/10 rounded px-2 py-1.5 text-white text-sm">
            <option value="carre">Carré (post FB/Insta)</option>
            <option value="story">Story / Reel (9:16)</option>
            <option value="porte">Affiche verticale</option>
            <option value="a4">Affiche murale A4/A3</option>
          </select>
        </label>
      </div>
      <div className="mt-3">
        <span className="text-xs text-zinc-400">Photos de référence (optionnel, max 4)</span>
        <input type="file" accept="image/*" multiple onChange={(e) => onFiles(e.target.files)} data-testid="pkh-poster-refs" className="mt-1 block w-full text-xs text-zinc-400 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-zinc-800 file:text-zinc-200" />
        {refs.length > 0 && <span className="text-[11px] text-emerald-400">{refs.length} photo(s) prête(s)</span>}
      </div>
      <button onClick={generate} disabled={busy} data-testid="pkh-poster-generate" className="mt-4 inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60" style={{ background: ROSE }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}{busy ? "Génération (~30-60 s)…" : "Générer l'affiche"}
      </button>
      <button onClick={saveTemplate} data-testid="pkh-poster-save-template" className="mt-4 ml-2 inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-4 py-2.5 rounded-lg text-sm">
        Enregistrer comme modèle
      </button>
      {result && (
        <div className="mt-5 grid md:grid-cols-2 gap-5" data-testid="pkh-poster-result">
          <img src={result.full_url || result.url} alt="Affiche PKH" className="w-full rounded-lg border border-zinc-800" data-testid="pkh-poster-image" />
          <div className="space-y-3">
            <textarea readOnly value={result.caption || ""} rows={7} data-testid="pkh-poster-caption" className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-xs text-zinc-200" />
            <div className="flex flex-wrap gap-2">
              <button onClick={copyCaption} data-testid="pkh-poster-copy" className="inline-flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded-lg"><Copy className="w-4 h-4" /> Copier la légende</button>
              <button onClick={shareFb} data-testid="pkh-poster-share" className="inline-flex items-center gap-1.5 text-sm bg-[#1877F2] text-white px-3 py-1.5 rounded-lg"><ExternalLink className="w-4 h-4" /> Partager sur Facebook</button>
              <a href={result.full_url || result.url} download target="_blank" rel="noopener noreferrer" data-testid="pkh-poster-download" className="inline-flex items-center gap-1.5 text-sm bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded-lg"><Download className="w-4 h-4" /> Télécharger</a>
            </div>
            <div className="flex flex-wrap gap-2 border-t border-zinc-800 pt-3">
              <a href={FB_PAGE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 text-rose-300 px-3 py-1.5 rounded-lg"><ExternalLink className="w-3.5 h-3.5" /> Page FB</a>
              <a href={FB_GROUP_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 text-rose-300 px-3 py-1.5 rounded-lg"><ExternalLink className="w-3.5 h-3.5" /> Groupe FB</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function QrStats() {
  const [stats, setStats] = useState(null);
  const load = useCallback(() => api.get("/poster/qr-stats").then((r) => setStats(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!stats) return null;
  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5" data-testid="pkh-qr-stats">
      <div className="flex items-center gap-2 mb-3"><QrCode size={18} style={{ color: ROSE }} /><h2 className="font-semibold text-white">Performance des codes QR (scans → inscriptions)</h2></div>
      <div className="flex gap-6 mb-3">
        <div><div className="text-xs text-zinc-400">Scans totaux</div><div className="text-2xl font-bold text-white" data-testid="qr-total-scans">{stats.total_scans}</div></div>
        <div><div className="text-xs text-zinc-400">Inscriptions rattachées</div><div className="text-2xl font-bold text-emerald-400" data-testid="qr-total-conv">{stats.total_conversions}</div></div>
      </div>
      {stats.rows.length === 0 ? <p className="text-zinc-500 text-sm">Aucun scan pour l'instant. Les scans de tes affiches (formation en ligne / PKH) apparaîtront ici.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-zinc-500 text-left text-xs uppercase"><th className="py-2 pr-4">Cible</th><th className="pr-4">Source</th><th className="pr-4">Scans</th><th>Inscriptions</th></tr></thead>
          <tbody>{stats.rows.map((r, i) => (
            <tr key={i} className="border-t border-white/5 text-zinc-200"><td className="py-2 pr-4">{r.to === "formation" ? "Formation en ligne" : "PowerKick Her"}</td><td className="pr-4 text-zinc-400">{r.src}</td><td className="pr-4">{r.scans}</td><td className="text-emerald-400">{r.conversions}</td></tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}

function ContentCalendar() {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ date: "", channel: "page", topic: "", caption: "", status: "planifie" });
  const [ideas, setIdeas] = useState([]);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const load = useCallback(() => api.get("/powerkick-her/content-calendar").then((r) => setPosts(r.data.posts || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const suggest = async () => {
    setLoadingIdeas(true);
    try { const r = await api.post("/powerkick-her/content-calendar/suggest"); setIdeas(r.data.ideas || []); }
    catch { toast.error("Impossible de générer des idées"); }
    finally { setLoadingIdeas(false); }
  };
  const addIdea = async (idea) => {
    try {
      await api.post("/powerkick-her/content-calendar", { date: idea.suggested_date || "", channel: idea.channel || "page", topic: idea.topic || "", caption: idea.caption || "", status: "planifie" });
      toast.success("Idée ajoutée au calendrier ✓"); setIdeas(ideas.filter((i) => i !== idea)); load();
    } catch { toast.error("Erreur"); }
  };

  const add = async () => {
    if (!form.date || !form.topic) { toast.error("Date et sujet requis"); return; }
    try { await api.post("/powerkick-her/content-calendar", form); toast.success("Publication planifiée ✓"); setForm({ date: "", channel: "page", topic: "", caption: "", status: "planifie" }); load(); }
    catch { toast.error("Erreur"); }
  };
  const toggleStatus = async (p) => {
    try { await api.put(`/powerkick-her/content-calendar/${p.id}`, { ...p, status: p.status === "publie" ? "planifie" : "publie" }); load(); } catch { toast.error("Erreur"); }
  };
  const del = async (id) => { try { await api.delete(`/powerkick-her/content-calendar/${id}`); load(); } catch { toast.error("Erreur"); } };

  const CH = { page: "Page FB", groupe: "Groupe FB", "les-deux": "Page + Groupe" };
  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-5" data-testid="pkh-calendar">
      <div className="flex items-center gap-2 mb-3"><CalendarBlank size={18} style={{ color: ROSE }} /><h2 className="font-semibold text-white flex-1">Calendrier de publications Facebook</h2>
        <button onClick={suggest} disabled={loadingIdeas} data-testid="cal-suggest" className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-rose-300 px-3 py-1.5 rounded-lg disabled:opacity-60">
          {loadingIdeas ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Suggérer des idées (IA)
        </button>
      </div>
      {ideas.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="cal-ideas">
          {ideas.map((idea, i) => (
            <div key={i} className="p-3 rounded-lg bg-rose-500/5 border border-rose-500/20" data-testid={`cal-idea-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-semibold text-white">💡 {idea.topic}</div>
                <button onClick={() => addIdea(idea)} data-testid={`cal-idea-add-${i}`} className="text-xs text-black font-semibold rounded px-2 py-1 shrink-0" style={{ background: ROSE }}>Ajouter</button>
              </div>
              <div className="text-xs text-zinc-400 whitespace-pre-wrap mt-1">{idea.caption}</div>
            </div>
          ))}
        </div>
      )}
      <div className="grid sm:grid-cols-5 gap-2 mb-4">
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="cal-date" className="bg-zinc-800 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
        <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="cal-channel" className="bg-zinc-800 border border-white/10 rounded px-2 py-1.5 text-white text-sm">
          <option value="page">Page FB</option><option value="groupe">Groupe FB</option><option value="les-deux">Page + Groupe</option>
        </select>
        <input placeholder="Sujet / idée" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="cal-topic" className="sm:col-span-2 bg-zinc-800 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
        <button onClick={add} data-testid="cal-add" className="text-black font-semibold rounded px-3 py-1.5 text-sm" style={{ background: ROSE }}>Ajouter</button>
        <textarea placeholder="Légende / notes (optionnel)" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid="cal-caption" rows={2} className="sm:col-span-5 bg-zinc-800 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
      </div>
      {posts.length === 0 ? <p className="text-zinc-500 text-sm">Aucune publication planifiée. Ajoute tes idées de posts pour la semaine ci-dessus.</p> : (
        <ul className="space-y-2" data-testid="cal-list">
          {posts.map((p) => (
            <li key={p.id} className="flex items-start gap-3 p-2 rounded-lg bg-white/5" data-testid={`cal-item-${p.id}`}>
              <button onClick={() => toggleStatus(p)} data-testid={`cal-toggle-${p.id}`} title="Basculer planifié/publié" className={`mt-0.5 text-xs px-2 py-0.5 rounded ${p.status === "publie" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{p.status === "publie" ? "Publié ✓" : "Planifié"}</button>
              <div className="flex-1">
                <div className="text-sm text-zinc-100">{p.date} · <span className="text-rose-300">{CH[p.channel] || p.channel}</span> · {p.topic}</div>
                {p.caption && <div className="text-xs text-zinc-400 whitespace-pre-wrap">{p.caption}</div>}
              </div>
              <button onClick={() => del(p.id)} data-testid={`cal-del-${p.id}`} className="text-red-400 hover:text-red-300"><Trash size={16} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const Inp = ({ label, v, on, testid }) => (
  <label className="text-xs text-zinc-400">{label}
    <input value={v} onChange={(e) => on(e.target.value)} data-testid={testid} className="mt-1 w-full bg-zinc-800 border border-white/10 rounded px-2 py-1.5 text-white text-sm" />
  </label>
);
